import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { GraduationCap, Loader2, MailCheck, AlertTriangle } from "lucide-react";
import { academy } from "@/lib/academy";
import { apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const CHECKS = [
  ["privacy", <>I have read and accept the <a href="/privacy" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">Privacy Notice</a>.</>],
  ["terms", <>I accept the <a href="/terms" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">Terms of Use</a>.</>],
  ["age", <>I confirm that I am at least 18 years old.</>],
  ["content", <>I understand that controversial topics, texts, situations and websites may appear during the course.</>],
];

export function AcademyLogin() {
  const [email, setEmail] = useState("");
  const [acc, setAcc] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const valid = /\S+@\S+\.\S+/.test(email) && CHECKS.every(([k]) => acc[k]);
  const submit = async () => {
    setBusy(true);
    try {
      await academy.post("/request-link", { email, accept_privacy: !!acc.privacy, accept_terms: !!acc.terms, accept_age: !!acc.age, accept_content: !!acc.content });
      setSent(true);
      setCooldown(60);
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const check = "mt-0.5 border-zinc-700 data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600";

  return (
    <div className="mx-auto w-full max-w-md rounded-2xl border border-zinc-800/70 bg-[#111113] p-7" data-testid="academy-login">
      <div className="mb-5 grid h-11 w-11 place-items-center rounded-xl bg-blue-600/15 text-blue-400"><GraduationCap size={21} /></div>
      <h2 className="text-2xl font-bold tracking-tight text-zinc-50">Start learning</h2>
      <p className="mt-2 text-[14px] text-zinc-400">Enter your email and we will send you a personal link. The link does not expire, so you can always come back to where you left off.</p>
      <div className="mt-5 flex gap-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[13px] text-amber-200" data-testid="academy-content-warning">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-400" />
        <span><b>Please note:</b> controversial topics, texts, situations and websites may appear during the course.</span>
      </div>
      <Input data-testid="academy-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
        onKeyDown={(e) => e.key === "Enter" && valid && !busy && cooldown <= 0 && submit()}
        className="mt-5 h-11 border-zinc-800 bg-zinc-950 text-[14px] text-zinc-100" />
      {CHECKS.map(([k, label]) => (
        <label key={k} className="mt-3 flex cursor-pointer items-start gap-2.5 text-[13px] text-zinc-400">
          <Checkbox data-testid={`academy-accept-${k}`} checked={!!acc[k]} onCheckedChange={(v) => setAcc((a) => ({ ...a, [k]: !!v }))} className={check} />
          <span>{label}</span>
        </label>
      ))}
      <button data-testid="academy-send-link" onClick={submit} disabled={!valid || busy || cooldown > 0}
        className="mt-6 inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 text-[14px] font-semibold text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40">
        {busy && <Loader2 size={16} className="animate-spin" />} {cooldown > 0 ? `Send again (${cooldown}s)` : "Email me my academy link"}
      </button>
      {sent && (
        <div className="mt-4 flex gap-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-[13px] text-emerald-200" data-testid="academy-link-sent">
          <MailCheck size={16} className="mt-0.5 shrink-0" /> Check your inbox at {email}. Open the link in the email to enter the academy.
        </div>
      )}
    </div>
  );
}
